import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Clock,
  MapPin,
  CalendarIcon,
  Star,
  Music,
  Mic2,
  Headphones,
  ChevronRight,
  Youtube,
} from "lucide-react";
import { useLocation } from "react-router-dom";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";

import { BUSINESS, TRANSLATIONS } from "@/config/business";
import { PLAYLIST_VIDEOS } from "@/data/playlist";

const WEBHOOK_URL = "https://automate.nirosha.org/webhook/ashastudios";

const SERVICES = [
  {
    id: "music",
    title: { en: "Music Recording & Mixing", mr: "संगीत रेकॉर्डिंग आणि मिक्सिंग" },
    description: {
      en: "Industry-standard multi-track recording using high-end preamps and converters. Our mixing service brings clarity, depth, and punch to your tracks, ensuring they sound competitive on all playback systems.",
      mr: "उच्च-श्रेणीचे प्रिएंप्स आणि कन्व्हर्टर वापरून उद्योग-मानक मल्टी-ट्रॅक रेकॉर्डिंग. आमची मिक्सिंग सेवा तुमच्या ट्रॅक्समध्ये स्पष्टता, खोली आणि जोम आणते."
    },
    image: "https://images.pexels.com/photos/6707302/pexels-photo-6707302.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: Music,
    span: "md:col-span-8 md:row-span-2"
  },
  {
    id: "mastering",
    title: { en: "Professional Mastering", mr: "व्यावसायिक मास्टरिंग" },
    description: {
      en: "The final polish your music deserves. We optimize your tracks for streaming, radio, and club play with surgical EQ and transparent compression.",
      mr: "तुमच्या संगीताला साजेशा अंतिम टच. आम्ही streaming, रेडिओ आणि क्लब प्लेसाठी तुमचे ट्रॅक्स ऑप्टिमाइझ करतो."
    },
    image: "https://images.unsplash.com/photo-1598488035139-bdbb2231ce04?q=80&w=2070&auto=format&fit=crop",
    icon: Headphones,
    span: "md:col-span-4 md:row-span-1"
  },
  {
    id: "podcast",
    title: { en: "Podcast Production", mr: "पॉडकास्ट उत्पादन" },
    description: {
      en: "Crystal-clear audio for your podcast with professional editing, noise reduction, and level matching. Perfect for creators who value quality.",
      mr: "व्यावसायिक संपादन, गोंगाट कमी करणे आणि लेव्हल मॅचिंगसह तुमच्या पॉडकास्टसाठी क्रिस्टल-डिअर ऑडिओ."
    },
    image: "https://images.pexels.com/photos/14540985/pexels-photo-14540985.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    icon: Mic2,
    span: "md:col-span-4 md:row-span-1"
  },
  {
    id: "voiceover",
    title: { en: "Voiceover & Dubbing", mr: "व्हॉइसओव्हर आणि डबिंग" },
    description: {
      en: "Ideal for commercials, audiobooks, and film. Our isolated vocal booth ensures zero background noise and perfect intimacy.",
      mr: "जाहिराती, ऑडिओबुक्स आणि चित्रपटांसाठी आदर्श. आमचे वेगळे व्होकल बूथ शून्य पार्श्वभूमी गोंगाट सुनिश्चित करते."
    },
    image: "https://images.unsplash.com/photo-1689771455000-7ca175aa03fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwyfHx2b2NhbCUyMGJvb3RoJTIwcmVjb3JkaW5nJTIwc3R1ZGlvfGVufDB8fHx8MTc3NDkzMDE5N3ww&ixlib=rb-4.1.0&q=85",
    icon: Headphones,
    span: "md:col-span-4 md:row-span-1"
  },
  {
    id: "composition",
    title: { en: "Music Composition", mr: "संगीत रचना" },
    description: {
      en: "Original background scores, jingles, and complete song arrangements tailored to your artistic vision or brand identity.",
      mr: "तुमच्या कलात्मक दृष्टी किंवा ब्रँड ओळखीनुसार तयार केलेले मूळ बॅकग्राउंड स्कोअर, जिंगल्स आणि गाणे व्यवस्था."
    },
    image: "https://images.pexels.com/photos/164821/pexels-photo-164821.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2",
    icon: Music,
    span: "md:col-span-4 md:row-span-1"
  },
  {
    id: "karaoke",
    title: { en: "Karaoke Recording", mr: "कराओके रेकॉर्डिंग" },
    description: {
      en: "Record your vocals over your favorite karaoke tracks with professional vocal processing, pitch correction, and high-quality mixing.",
      mr: "तुमच्या आवडत्या कराओके ट्रॅकवर व्यावसायिक व्होकल प्रोसेसिंग, पिच करेक्शन आणि उच्च-दर्जाच्या मिक्सिंगसह तुमचे गाणे रेकॉर्ड करा."
    },
    image: "/images/karaoke_bg.png",
    icon: Mic2,
    span: "md:col-span-4 md:row-span-1"
  }
];

const EQUIPMENT = [
  {
    category: { en: "Microphones", mr: "मायक्रोफोन्स" },
    items: ["AKG C414 XLS", "Shure SM7B", "Sennheiser MD421", "AKG P420 (x2)", "Shure SM57, SM58", "Rode NT2", "Mipro", "Rode Pencil Mic."]
  },
  {
    category: { en: "Monitoring", mr: "मॉनिटरिंग" },
    items: ["Yamaha HS5", "Yamaha Woofer HS8"]
  },
  {
    category: { en: "Outboard Gear", mr: "आउटबोर्ड गिअर" },
    items: [
      "Focusrite 5th Gen Audio Interface with 16 Audio Outputs",
      "Focusrite Scarlet 2i2",
      "M-Audio Oxygen 61",
      "MIDI Interface",
      "Korg PA700 Professional Arranger"
    ]
  },
  {
    category: { en: "DAW", mr: "DAW" },
    items: ["Cubase 14", "FL Studio 2025"]
  },
  {
    category: { en: "Plugins", mr: "प्लगइन्स" },
    items: [
      "Kontakt 8",
      "Halion 7",
      "EZdrummer 3",
      "The Grand 3 64 Bit",
      "Halion Sonic",
      "Steinberg Library",
      "Omnisphere",
      "Melodyne 5",
      "Groove Agent",
      "Wavepad Sound Editor"
    ]
  },
  {
    category: { en: "Headphones", mr: "हेडफोन्स" },
    items: ["Sennheiser HD599 (Dual)", "Sennheiser HD205"]
  }
];

const TESTIMONIALS = [
  {
    name: "Kartiki Gaikwad",
    role: { en: "Renowned Vocalist & Composer", mr: "प्रसिद्ध गायिका आणि संगीतकार" },
    content: {
      en: "Asha Studios is an absolute gem in Uruli Kanchan. The recording quality, vocal clarity, and mixing are truly world-class. It is a wonderful experience recording here!",
      mr: "उरुळी कांचनमधील आशा स्टुडिओज एक अप्रतिम रेकॉर्डिंग स्टुडिओ आहे. येथील रेकॉर्डिंग गुणवत्ता, व्होकल क्लेरिटी आणि मिक्सिंग खरोखरच जागतिक दर्जाचे आहे. येथे रेकॉर्ड करण्याचा अनुभव अतिशय सुंदर होता!"
    },
    rating: 5
  },
  {
    name: "Geetanjali Khandekar",
    role: { en: "Vocalist & Creator", mr: "गायिका आणि क्रिएटर" },
    content: {
      en: "Extremely proud to have such a state-of-the-art recording facility right in our Uruli Kanchan! Fantastic acoustics, high-end gear, and a highly cooperative team.",
      mr: "आपल्या उरुळी कांचनमध्ये इतका सुंदर ऑडिओ स्टुडिओ! येथे इतकी अत्याधुनिक रेकॉर्डिंग सुविधा असल्याचा अभिमान वाटतो. अप्रतिम अकौस्टिक्स, दर्जेदार गियर आणि अत्यंत सहकार्य करणारी टीम."
    },
    rating: 5
  },
  {
    name: "Usha Shinde",
    role: { en: "Vocalist", mr: "गायिका" },
    content: {
      en: "Excellent recording experience! The sound clarity and professional vocal processing make it the perfect place for both beginner and expert singers. Highly recommended!",
      mr: "उत्कृष्ट अनुभव! अप्रतिम साउंड क्लॅरिटी आणि व्यावसायिक व्होकल प्रोसेसिंगमुळे नवशिक्या तसेच अनुभवी गायकांसाठी ही एक उत्तम जागा आहे. नक्की भेट द्या!"
    },
    rating: 5
  }
];

const FAQS = [
  {
    question: { en: "How do I book a session?", mr: "मी सत्र कसे बुक करू?" },
    answer: {
      en: "You can book directly through our website form, or reach out via WhatsApp/Phone. A 25% deposit is required to confirm your slot.",
      mr: "तुम्ही आमच्या वेबसाइट फॉर्मद्वारे थेट बुक करू शकता किंवा व्हॉट्सॲप/फोनद्वारे संपर्क साधू शकता. स्लॉट कन्फर्म करण्यासाठी २५% अनामत रक्कम आवश्यक आहे."
    }
  },
  {
    question: { en: "Do you provide instruments?", mr: "तुम्ही वाद्ये प्रदान करता का?" },
    answer: {
      en: "Yes, we have a basic selection of acoustic/electric guitars and a MIDI controller available for use. Residents are encouraged to bring their own primary instruments for comfort.",
      mr: "हो, आमच्याकडे वापरासाठी अकौस्टिक/इलेक्ट्रिक गिटार आणि MIDI कंट्रोलरची मूलभूत निवड उपलब्ध आहे."
    }
  },
  {
    question: { en: "What is your turnaround time for mixing?", mr: "मिक्सिंगसाठी लागणारा वेळ किती आहे?" },
    answer: {
      en: "For a single track, our turnaround is 3-5 business days. This includes two rounds of revisions to ensure you're 100% happy with the result.",
      mr: "एका ट्रॅकसाठी, आमचा वेळ ३-५ कामकाजाचे दिवस आहे. यामध्ये तुमच्या समाधानासाठी दोन फेऱ्यांचा समावेश आहे."
    }
  },
  {
    question: { en: "Can I bring my own engineer?", mr: "मी माझा स्वतःचा इंजिनीअर आणू शकतो का?" },
    answer: {
      en: "Absolutely! We offer dry-hire rates if you want to use the facility with your own producer or engineer. Our staff will be present for technical support.",
      mr: "नक्कीच! जर तुम्हाला तुमच्या स्वतःच्या प्रोड्युसर किंवा इंजिनीअरसह सुविधा वापरायची असेल तर आम्ही ड्राय-हायर दर ऑफर करतो."
    }
  },
  {
    question: { en: "I am a beginner or first-time singer. Can I record here?", mr: "मी नवशिक्या किंवा प्रथमच गाणारा गायक आहे. मी येथे रेकॉर्डिंग करू शकतो का?" },
    answer: {
      en: "Absolutely! We welcome everyone from absolute beginners and first-time singers to seasoned experts. Our studio is a completely judgment-free space. Our supportive team will guide you step-by-step to help you feel relaxed and confident to deliver your best performance.",
      mr: "नक्कीच! आम्ही अगदी नवशिक्या आणि पहिल्यांदा गाणाऱ्यांपासून ते अनुभवी गायकांपर्यंत सर्वांचे स्वागत करतो. आमचा स्टुडिओ पूर्णपणे जजमेंट-फ्री (भेदभावमुक्त) झोन आहे. आमची सपोर्टिव्ह टीम तुम्हाला तुमच्या कम्फर्टनुसार आणि आत्मविश्वासाने तुमचे सर्वोत्तम सादरीकरण करण्यासाठी संपूर्ण प्रक्रियेत मार्गदर्शन करेल."
    }
  },
  {
    question: { en: "Do you offer Karaoke Recording? Can you help with tracks?", mr: "तुम्ही कराओके रेकॉर्डिंगची सुविधा देता का? कराओके ट्रॅकसाठी मदत मिळू शकेल का?" },
    answer: {
      en: "Yes! We offer premium Karaoke Recording. If you don't have a track, we can assist you in finding, sourcing, or preparing high-quality karaoke tracks. We will also process and mix your vocals to make them sound professional and sit perfectly with the music.",
      mr: "होय! आम्ही दर्जेदार कराओके रेकॉर्डिंग सेवा देतो. तुमच्याकडे कराओके ट्रॅक नसेल, तर आम्ही तुम्हाला दर्जेदार ट्रॅक शोधण्यात, मिळवण्यात किंवा तयार करण्यात मदत करू शकतो. तसेच तुमच्या आवाजाला ट्रॅकसोबत परिपूर्ण बनवण्यासाठी आम्ही त्यावर व्यावसायिक प्रोसेसिंग आणि मिक्सिंग देखील करतो."
    }
  }
];

const DURATIONS = {
  en: ["1 Hour", "2 Hours", "3 Hours", "Half Day (4 Hours)", "Full Day (8 Hours)"],
  mr: ["१ तास", "२ तास", "३ तास", "अर्धा दिवस (४ तास)", "पूर्ण दिवस (८ तास)"]
};

const BUDGETS = {
  en: ["Under ₹2,000", "₹2,000 - ₹5,000", "₹5,000 - ₹10,000", "₹10,000 - ₹20,000", "₹20,000+"],
  mr: ["₹२,००० खाली", "₹२,००० - ₹५,०००", "₹५,००० - ₹१०,०००", "₹१०,००० - ₹२०,०००", "₹२०,०००+"]
};

const MARQUEE_ITEMS = {
  en: ["RECORDING", "MIXING", "MASTERING", "PODCASTS", "VOICEOVER", "DUBBING", "KARAOKE"],
  mr: ["रेकॉर्डिंग", "मिक्सिंग", "मास्टरिंग", "पॉडकास्ट", "व्हॉइसओव्हर", "डबिंग", "कराओके"]
};

// Hero Section with 3D Parallax
function HeroSection({ lang }) {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const t = (key) => TRANSLATIONS[lang][key] || key;

  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 2;
      const y = (e.clientY / window.innerHeight - 0.5) * 2;
      setMousePos({ x, y });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[110vh] flex items-center overflow-hidden bg-[#0A0A0B]"
    >
      {/* Layer 1: Background (Deep Parallax) */}
      <div
        className="absolute inset-0 z-0 transition-transform duration-300 ease-out scale-110"
        style={{
          transform: `translate(${mousePos.x * 10}px, ${mousePos.y * 10}px) scale(1.1)`,
          backgroundImage: "url('/images/studio_bg.png')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Layer 2: Midground Elements (Medium Parallax) */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{
          transform: `translate(${mousePos.x * -25}px, ${mousePos.y * -25}px)`,
        }}
      >
        <div className="absolute top-1/2 right-10 lg:right-32 -translate-y-1/2 w-[40%] h-[80%] max-w-[600px]">
          <img
            src="/images/mic_fg.png"
            alt="Studio Mic"
            className="w-full h-full object-contain opacity-80 mix-blend-screen drop-shadow-[0_0_30px_rgba(212,175,55,0.3)] animate-float"
          />
        </div>
      </div>

      {/* Hero Overlay Gradient */}
      <div className="absolute inset-0 z-30 hero-overlay pointer-events-none" />

      {/* Content Layer */}
      <div className="relative z-40 max-w-7xl mx-auto px-6 sm:px-12 py-32 w-full">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-8 animate-fadeInUp">
            <div className="flex items-center gap-1.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={18}
                  className={i < Math.floor(BUSINESS.rating) ? "star-filled fill-current" : "text-gray-600"}
                />
              ))}
            </div>
            <span className="text-sm font-medium tracking-wide text-gray-200">
              <span className="text-white font-bold">{BUSINESS.rating}</span> {t("googleRating")}
            </span>
          </div>

          <div className="overflow-hidden mb-4">
            <p className="text-sm tracking-[0.4em] uppercase font-bold text-[#D4AF37] animate-slideRight">
              {t("premiumExperience")}
            </p>
          </div>

          <h1 className="text-6xl sm:text-7xl lg:text-8xl tracking-tighter font-black text-white mb-8 leading-[0.9]">
            <span className="block animate-fadeInUp delay-200">
              {t("heroTitle_1")}
            </span>
            <span className="text-transparent stroke-text animate-fadeInUp delay-300 block mt-2">
              {t("heroTitle_2")}
            </span>
          </h1>

          <p className="text-xl text-gray-200 leading-relaxed mb-12 max-w-xl animate-fadeInUp delay-500">
            {t("heroSubtitle")}
          </p>

          <div className="flex flex-col sm:flex-row gap-6 animate-fadeInUp delay-600">
            <a
              href="#booking"
              className="btn-primary px-10 py-5 rounded-md text-center font-bold flex items-center justify-center gap-3 shadow-xl shadow-[#D4AF37]/10"
            >
              {t("bookNow")}
              <ChevronRight size={22} />
            </a>
            <a
              href="#services"
              className="btn-outline px-10 py-5 rounded-md text-center font-semibold bg-white/5 backdrop-blur-sm"
            >
              {t("exploreServices")}
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-3">
        <span className="text-[10px] tracking-[0.3em] font-bold text-gray-500 uppercase">{t("scrollDown")}</span>
        <div className="w-[1px] h-12 bg-gradient-to-b from-[#D4AF37] to-transparent animate-bounce-slow" />
      </div>
    </section>
  );
}

// Marquee Section
function MarqueeSection({ lang }) {
  const items = MARQUEE_ITEMS[lang];

  return (
    <div
      data-testid="marquee-section"
      className="bg-[#141415] py-4 border-y border-white/5"
    >
      <div className="marquee-container">
        <div className="marquee-content">
          {[...items, ...items, ...items].map((item, i) => (
            <span key={i} className="inline-flex items-center mx-8">
              <span className="text-sm tracking-[0.3em] font-semibold text-[#D4AF37]">
                {item}
              </span>
              <span className="ml-8 text-[#D4AF37]">•</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Services Section
function ServicesSection({ lang }) {
  const t = (key) => TRANSLATIONS[lang][key] || key;
  return (
    <section id="services" className="py-24 sm:py-32 bg-[#0A0A0B]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">
            {t("ourServices")}
          </p>
          <h2 className="text-4xl sm:text-5xl tracking-tight font-bold text-white">
            {t("audioSolutions")}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 sm:gap-8">
          {SERVICES.map((service) => (
            <div
              key={service.id}
              className={`service-card rounded-2xl ${service.span} min-h-[300px] md:min-h-[250px]`}
            >
              <img
                src={service.image}
                alt={service.title[lang]}
                className="absolute inset-0 w-full h-full object-cover rounded-2xl"
              />
              <div className="relative z-10 h-full flex flex-col justify-end p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                    <service.icon size={20} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="text-2xl sm:text-3xl tracking-tight font-bold text-white">
                    {service.title[lang]}
                  </h3>
                </div>
                <p className="text-gray-200 text-base leading-relaxed">
                  {service.description[lang]}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Equipment Section
function EquipmentSection({ lang }) {
  const t = (key) => TRANSLATIONS[lang][key] || key;
  const [activeTab, setActiveTab] = useState(0);

  return (
    <section id="equipment" className="py-24 sm:py-32 bg-[#0A0A0B]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div className="relative group">
            <img src="/images/gear_rack.png" alt="Studio gear" className="rounded-2xl w-full h-[550px] object-cover grayscale hover:grayscale-0 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0B] via-transparent to-transparent opacity-60" />
            <div className="absolute top-6 left-6 glass-panel p-4 rounded-xl backdrop-blur-md">
              <span className="text-[#D4AF37] font-bold text-lg">{t("analogSoul")}</span>
            </div>
          </div>
          <div className="flex flex-col justify-center min-h-[450px]">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">{t("ourHardware")}</p>
            <h2 className="text-4xl sm:text-5xl tracking-tight font-bold text-white mb-8">{t("sonicExcellence")}</h2>
            
            {/* Category Selector Pills */}
            <div className="flex flex-wrap gap-2.5 mb-8">
              {EQUIPMENT.map((cat, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTab(i)}
                  className={`px-4 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${
                    activeTab === i
                      ? "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/60 shadow-[0_0_15px_rgba(212,175,55,0.1)]"
                      : "bg-white/5 text-gray-400 border-white/5 hover:border-white/10 hover:text-white"
                  }`}
                >
                  {cat.category[lang]}
                </button>
              ))}
            </div>

            {/* Active Tab Content Card List */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              {EQUIPMENT[activeTab].items.map((item, j) => (
                <div
                  key={j}
                  className="glass-panel px-4 py-3.5 rounded-xl border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-300 flex items-center gap-3 group/item hover:transform hover:translate-x-1"
                >
                  <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] group-hover/item:scale-125 transition-transform" />
                  <span className="text-gray-300 group-hover/item:text-white text-sm font-medium transition-colors">
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// About Section
function AboutSection({ lang }) {
  const t = (key) => TRANSLATIONS[lang][key] || key;
  return (
    <section id="about" className="py-24 sm:py-32 bg-[#141415]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">{t("aboutUs")}</p>
            <h2 className="text-4xl sm:text-5xl tracking-tight font-bold text-white mb-6">{t("yourSound")}</h2>
            <p className="text-gray-200 leading-relaxed mb-6">{t("aboutText_1")}</p>
            <p className="text-gray-200 leading-relaxed mb-8">{t("aboutText_2")}</p>
            <div className="grid grid-cols-2 gap-6">
              <div className="glass-panel rounded-xl p-5">
                <div className="text-3xl font-black text-[#D4AF37] mb-1">5+</div>
                <div className="text-sm text-gray-300">{t("yearsExperience")}</div>
              </div>
              <div className="glass-panel rounded-xl p-5">
                <div className="text-3xl font-black text-[#D4AF37] mb-1">500+</div>
                <div className="text-sm text-gray-300">{t("projectsCompleted")}</div>
              </div>
            </div>
          </div>
          <div className="relative">
            <img src="https://images.unsplash.com/photo-1709846486154-f9172678be6f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwxfHxyZWNvcmRpbmclMjBzdHVkaW8lMjBtaWNyb3Bob25lJTIwZGFyayUyMG1vb2R5fGVufDB8fHx8MTc3NDkzMDE5NXww&ixlib=rb-4.1.0&q=85" alt="Studio microphone" className="rounded-2xl w-full h-[400px] lg:h-[500px] object-cover" />
            <div className="absolute -bottom-6 -left-6 glass-panel rounded-xl p-5 hidden sm:block">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (<Star key={i} size={16} className="star-filled fill-current" />))}
                </div>
                <span className="text-white font-semibold">{BUSINESS.rating}</span>
              </div>
              <p className="text-sm text-gray-300 mt-1">{t("googleRating")}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Testimonials Section
function TestimonialsSection({ lang }) {
  const t = (key) => TRANSLATIONS[lang][key] || key;
  return (
    <section id="testimonials" className="py-24 sm:py-32 bg-[#0A0A0B] overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">{t("hearFromClients")}</p>
          <h2 className="text-4xl sm:text-5xl tracking-tight font-bold text-white">{t("successStoriesHeader")}</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {TESTIMONIALS.map((t_item, i) => (
            <div key={i} className="glass-panel rounded-2xl p-8 hover:transform hover:-translate-y-2 transition-all duration-500">
              <div className="flex gap-1 mb-6">{[...Array(t_item.rating)].map((_, j) => (<Star key={j} size={14} className="star-filled fill-current" />))}</div>
              <p className="text-gray-200 italic mb-8 leading-relaxed">"{t_item.content[lang]}"</p>
              <div className="mt-auto">
                <p className="text-white font-bold">{t_item.name}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-12 text-center">
          <a
            href="https://share.google/A3BlUz8ConVrJGVxx"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-[#D4AF37]/30 hover:border-[#D4AF37] bg-white/5 hover:bg-[#D4AF37]/10 text-[#D4AF37] font-semibold text-sm transition-all group"
          >
            <span>{lang === "en" ? "Read More Reviews on Google" : "गुगलवर अधिक रिव्ह्यूज वाचा"}</span>
            <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>
      </div>
    </section>
  );
}

// Booking Section
function BookingSection({ lang }) {
  const [date, setDate] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const t = (key) => TRANSLATIONS[lang][key] || key;
  const [formData, setFormData] = useState({
    name: "", phone: "", email: "", time: "",
    service_type: "", duration: "", budget: "", message: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !date || !formData.time || !formData.service_type) {
      toast.error(t("fillRequired"));
      return;
    }
    setIsSubmitting(true);
    try {
      const bookingData = { ...formData, date: format(date, "yyyy-MM-dd") };
      await axios.post(WEBHOOK_URL, bookingData);
      toast.success(t("bookingSuccess"));
      setFormData({ name: "", phone: "", email: "", time: "", service_type: "", duration: "", budget: "", message: "" });
      setDate(null);
    } catch (error) {
      console.error("Booking error:", error);
      toast.error(t("bookingError"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const timeSlots = ["11:00 AM", "12:00 PM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM", "5:00 PM"];
  const durations = DURATIONS[lang];
  const budgets = BUDGETS[lang];

  return (
    <section id="booking" className="py-24 sm:py-32 bg-[#0A0A0B]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">{t("bookNow")}</p>
            <h2 className="text-4xl sm:text-5xl tracking-tight font-bold text-white mb-6">{t("readyToCreate")}</h2>
            <p className="text-gray-200 leading-relaxed mb-8">{t("bookingSubtitle")}</p>
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0"><Clock size={20} className="text-[#D4AF37]" /></div>
                <div>
                  <h4 className="text-lg font-medium text-white mb-1">{t("workingHours")}</h4>
                  <p className="text-gray-300 text-sm">Mon - Sat: {BUSINESS.hours.weekdays}<br />Sunday: {BUSINESS.hours.sunday}</p>
                </div>
              </div>
              <a
                href={BUSINESS.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 hover:opacity-85 transition-opacity group"
              >
                <div className="w-10 h-10 rounded-full bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform"><MapPin size={20} className="text-[#D4AF37]" /></div>
                <div>
                  <h4 className="text-lg font-medium text-white mb-1 flex items-center gap-1.5">
                    {t("location")}
                    <span className="text-xs text-[#D4AF37] font-normal opacity-0 group-hover:opacity-100 transition-opacity">({t("openInMaps")})</span>
                  </h4>
                  <p className="text-gray-300 text-sm">{BUSINESS.address}</p>
                </div>
              </a>
            </div>
          </div>
          <div className="glass-panel rounded-2xl p-6 sm:p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">{t("fullName")}</label>
                  <Input name="name" value={formData.name} onChange={handleInputChange} placeholder={t("namePlaceholder")} className="form-input w-full px-4 py-3 rounded-md bg-black/40 border-white/10 text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">{t("phoneNumber")}</label>
                  <Input name="phone" value={formData.phone} onChange={handleInputChange} placeholder={t("phonePlaceholder")} className="form-input w-full px-4 py-3 rounded-md bg-black/40 border-white/10 text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">{t("emailAddress")}</label>
                <Input name="email" type="email" value={formData.email} onChange={handleInputChange} placeholder={t("emailPlaceholder")} className="form-input w-full px-4 py-3 rounded-md bg-black/40 border-white/10 text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">{t("preferredDate")}</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal bg-black/40 border-white/10 text-white hover:bg-black/60 hover:text-white px-4 py-3 h-auto">
                        <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" /><br />
                        {date ? format(date, "PPP") : <span className="text-gray-500">{t("selectDate")}</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#1A1A1C] border-white/10" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate} initialFocus disabled={(date) => date < new Date()} className="bg-[#1A1A1C] text-white" />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">{t("preferredTime")}</label>
                  <Select value={formData.time} onValueChange={(value) => handleSelectChange("time", value)}>
                    <SelectTrigger className="w-full bg-black/40 border-white/10 text-white px-4 py-3 h-auto focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"><SelectValue placeholder={t("selectTime")} /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1C] border-white/10">
                      {timeSlots.map((time) => (<SelectItem key={time} value={time} className="text-white hover:bg-white/10 hover:text-[#D4AF37] focus:bg-white/10 focus:text-[#D4AF37]">{time}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">{t("serviceType")}</label>
                <Select value={formData.service_type} onValueChange={(value) => handleSelectChange("service_type", value)}>
                  <SelectTrigger className="w-full bg-black/40 border-white/10 text-white px-4 py-3 h-auto focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"><SelectValue placeholder={t("selectService")} /></SelectTrigger>
                  <SelectContent className="bg-[#1A1A1C] border-white/10">
                    {SERVICES.map((service) => (<SelectItem key={service.id} value={service.id} className="text-white hover:bg-white/10 hover:text-[#D4AF37] focus:bg-white/10 focus:text-[#D4AF37]">{service.title[lang]}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">{t("duration")}</label>
                  <Select value={formData.duration} onValueChange={(value) => handleSelectChange("duration", value)}>
                    <SelectTrigger className="w-full bg-black/40 border-white/10 text-white px-4 py-3 h-auto focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"><SelectValue placeholder={t("selectDuration")} /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1C] border-white/10">
                      {durations.map((duration) => (<SelectItem key={duration} value={duration} className="text-white hover:bg-white/10 hover:text-[#D4AF37] focus:bg-white/10 focus:text-[#D4AF37]">{duration}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-200 mb-2">{t("budgetRange")}</label>
                  <Select value={formData.budget} onValueChange={(value) => handleSelectChange("budget", value)}>
                    <SelectTrigger className="w-full bg-black/40 border-white/10 text-white px-4 py-3 h-auto focus:ring-[#D4AF37]/20 focus:border-[#D4AF37]"><SelectValue placeholder={t("selectBudget")} /></SelectTrigger>
                    <SelectContent className="bg-[#1A1A1C] border-white/10">
                      {budgets.map((budget) => (<SelectItem key={budget} value={budget} className="text-white hover:bg-white/10 hover:text-[#D4AF37] focus:bg-white/10 focus:text-[#D4AF37]">{budget}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-200 mb-2">{t("additionalMessage")}</label>
                <Textarea name="message" value={formData.message} onChange={handleInputChange} placeholder={t("placeholderMessage")} rows={4} className="form-input w-full px-4 py-3 rounded-md bg-black/40 border-white/10 text-white placeholder:text-gray-500 focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 resize-none" />
              </div>
              <Button type="submit" disabled={isSubmitting} className="w-full btn-primary py-4 h-auto text-base font-semibold rounded-md">{isSubmitting ? t("submitting") : t("submitBooking")}</Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

// FAQ Section
function FAQSection({ lang }) {
  const t = (key) => TRANSLATIONS[lang][key] || key;
  return (
    <section id="faq" className="py-24 sm:py-32 bg-[#141415]">
      <div className="max-w-3xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">{t("gotQuestions")}</p>
          <h2 className="text-4xl sm:text-5xl tracking-tight font-bold text-white">{t("commonInquiries")}</h2>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {FAQS.map((faq, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="border-b border-white/10">
              <AccordionTrigger className="text-left font-bold text-white hover:text-[#D4AF37] hover:no-underline transition-all py-5 text-lg sm:text-xl">
                {faq.question[lang]}
              </AccordionTrigger>
              <AccordionContent className="text-gray-400 leading-relaxed text-base pb-5">
                {faq.answer[lang]}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

// Portfolio / Releases Section
function PortfolioSection({ lang }) {
  const [activeVideoId, setActiveVideoId] = useState(PLAYLIST_VIDEOS[0].id);
  const t = (key) => TRANSLATIONS[lang][key] || key;

  const activeIndex = PLAYLIST_VIDEOS.findIndex((v) => v.id === activeVideoId) + 1;

  return (
    <section id="releases" className="py-24 sm:py-32 bg-[#0A0A0B]">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        <div className="text-center mb-16">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">
            {t("releases")}
          </p>
          <h2 className="text-4xl sm:text-5xl tracking-tight font-bold text-white mb-6">
            {t("featuredReleases")}
          </h2>
          <p className="text-gray-300 leading-relaxed max-w-2xl mx-auto text-base sm:text-lg">
            {t("portfolioSubtitle")}
          </p>
        </div>

        {/* Layout Grid: Stack on mobile, Player (left) and Playlist (right) on desktop */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Main Video Player (lg:col-span-8) */}
          <div className="lg:col-span-8 space-y-4">
            <div className="relative overflow-hidden rounded-2xl border border-white/10 hover:border-[#D4AF37]/20 transition-all duration-500 shadow-2xl pb-[56.25%] h-0 w-full">
              <iframe
                src={`https://www.youtube.com/embed/${activeVideoId}?autoplay=1&mute=0`}
                title="Asha Studios Video Player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="absolute top-0 left-0 w-full h-full border-0"
              />
            </div>
            {/* Active Video Info */}
            <div className="glass-panel p-6 rounded-2xl border border-white/5">
              <h3 className="text-xl sm:text-2xl font-bold text-white leading-snug">
                {PLAYLIST_VIDEOS.find((v) => v.id === activeVideoId)?.title}
              </h3>
              <p className="text-sm text-[#D4AF37] font-semibold mt-2.5">
                {BUSINESS.name} • {activeIndex} / {PLAYLIST_VIDEOS.length}
              </p>
            </div>
          </div>

          {/* Playlist Panel Column (lg:col-span-4) */}
          <div className="lg:col-span-4 bg-[#141415] rounded-2xl border border-white/10 shadow-2xl flex flex-col h-[400px] lg:h-[530px] overflow-hidden">
            {/* Playlist Header */}
            <div className="p-4 sm:p-5 border-b border-white/10 bg-white/5 flex flex-col gap-1 flex-shrink-0">
              <h4 className="font-bold text-white text-base">Asha Studios Playlist</h4>
              <p className="text-xs text-gray-400">
                {BUSINESS.name} • {activeIndex} / {PLAYLIST_VIDEOS.length}
              </p>
            </div>

            {/* Playlist Scroll Area */}
            <div className="flex-1 overflow-y-auto divide-y divide-white/5 max-h-[350px] lg:max-h-none">
              {PLAYLIST_VIDEOS.map((video, idx) => {
                const isActive = video.id === activeVideoId;
                return (
                  <div
                    key={video.id}
                    onClick={() => setActiveVideoId(video.id)}
                    className={`flex items-center gap-3.5 p-3.5 cursor-pointer transition-all ${
                      isActive
                        ? "bg-[#D4AF37]/10 hover:bg-[#D4AF37]/15 border-l-4 border-[#D4AF37]"
                        : "hover:bg-white/5 border-l-4 border-transparent"
                    }`}
                  >
                    {/* Index Number or Active icon */}
                    <div className="w-5 text-center flex-shrink-0">
                      {isActive ? (
                        <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] mx-auto animate-ping" />
                      ) : (
                        <span className="text-xs text-gray-500 font-semibold">{idx + 1}</span>
                      )}
                    </div>

                    {/* Thumbnail */}
                    <div className="relative w-28 aspect-video rounded-md overflow-hidden bg-black/60 flex-shrink-0">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <span className="absolute bottom-1 right-1 bg-black/85 text-[10px] text-white font-bold px-1 py-0.5 rounded leading-none">
                        {video.duration}
                      </span>
                    </div>

                    {/* Title */}
                    <div className="flex-1 min-w-0">
                      <p
                        className={`text-sm font-semibold leading-snug line-clamp-2 ${
                          isActive ? "text-[#D4AF37]" : "text-gray-200"
                        }`}
                        title={video.title}
                      >
                        {video.title}
                      </p>
                      <p className="text-[11px] text-gray-500 font-medium mt-1">Asha Studios</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Global YouTube Link Button */}
        <div className="mt-12 text-center">
          <a
            href="https://www.youtube.com/watch?v=AZ3v285rLYA&list=PLy8PZL2BsDRsD0Mx6siYOSu9wq9hsxaJx"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-outline px-7 py-3.5 rounded-full inline-flex items-center gap-2.5 text-sm font-semibold hover:bg-white/5"
          >
            <Youtube size={18} className="text-[#D4AF37]" />
            <span>{t("openPlaylist")}</span>
          </a>
        </div>
      </div>
    </section>
  );
}

// Main Home Page Component
export default function Home({ lang }) {
  const location = useLocation();

  useEffect(() => {
    const scrollTo = location.state?.scrollTo;
    if (scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }, 100);
    } else if (window.location.hash) {
      // Handle fallback hash navigation
      const hash = window.location.hash;
      const parts = hash.split("/");
      const id = parts[parts.length - 1];
      if (id && id !== "gallery") {
        setTimeout(() => {
          const element = document.getElementById(id);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      }
    }
  }, [location]);

  return (
    <>
      <HeroSection lang={lang} />
      <MarqueeSection lang={lang} />
      <ServicesSection lang={lang} />
      <EquipmentSection lang={lang} />
      <AboutSection lang={lang} />
      <PortfolioSection lang={lang} />
      <TestimonialsSection lang={lang} />
      <BookingSection lang={lang} />
      <FAQSection lang={lang} />
    </>
  );
}
